import { supabase } from '@/integrations/supabase/client';
import { 
  getPendingInspections, 
  updateInspectionSyncStatus, 
  deleteOfflineInspection,
  getSyncQueue,
  removeFromSyncQueue 
} from './offlineDB';
import { Json } from '@/integrations/supabase/types';

interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: string[];
}

class BackgroundSyncManager {
  private isSyncing = false;
  private syncInterval: number | null = null;
  private listeners: Set<(status: 'syncing' | 'idle' | 'error') => void> = new Set();
  private periodicSyncConsumers = 0;
  private readonly onlineHandler = () => {
    void this.triggerSync();
  };

  constructor() {
    // Listen for online events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.onlineHandler);
    }
  }

  addListener(callback: (status: 'syncing' | 'idle' | 'error') => void) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private notifyListeners(status: 'syncing' | 'idle' | 'error') {
    this.listeners.forEach(listener => listener(status));
  }

  async triggerSync(): Promise<SyncResult> {
    if (typeof navigator === 'undefined' || this.isSyncing || !navigator.onLine) {
      return { success: false, synced: 0, failed: 0, errors: ['Sync already in progress or offline'] };
    }

    this.isSyncing = true;
    this.notifyListeners('syncing');

    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    try {
      // Sync offline inspections
      const pendingInspections = await getPendingInspections();
      
      for (const inspection of pendingInspections) {
        try {
          await updateInspectionSyncStatus(inspection.id, 'syncing');
          
          // Call the checkout RPC
          const { error } = await supabase.rpc('registrar_checkout', {
            p_inspecao_mobile_id: inspection.id,
            p_checklist_json: inspection.checklistJson as Json,
            p_fotos: inspection.fotos,
            p_upload_mode: 'offline',
            p_offline_justification: inspection.offlineJustification || 'Sincronizado automaticamente após reconexão'
          });

          if (error) throw error;

          await updateInspectionSyncStatus(inspection.id, 'synced');
          await deleteOfflineInspection(inspection.id);
          result.synced++;
          
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          result.errors.push(`Inspection ${inspection.id}: ${errorMessage}`);
          await updateInspectionSyncStatus(inspection.id, 'failed', errorMessage);
          result.failed++;
          
          // Don't retry if max retries reached
          if (inspection.retryCount >= 3) {
            console.error(`Max retries reached for inspection ${inspection.id}`);
          }
        }
      }

      // Process sync queue
      const syncQueue = await getSyncQueue();
      
      for (const item of syncQueue) {
        try {
          // Process based on type
          switch (item.type) {
            case 'photo':
              // Handle photo sync
              await this.syncPhoto(item.payload);
              break;
            case 'checklist':
              // Handle checklist sync
              await this.syncChecklist(item.payload);
              break;
          }
          
          await removeFromSyncQueue(item.id);
          result.synced++;
          
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          result.errors.push(`Queue item ${item.id}: ${errorMessage}`);
          result.failed++;
        }
      }

      result.success = result.failed === 0;
      this.notifyListeners(result.success ? 'idle' : 'error');
      
    } catch (error) {
      result.success = false;
      result.errors.push(error instanceof Error ? error.message : 'Unknown sync error');
      this.notifyListeners('error');
    } finally {
      this.isSyncing = false;
    }

    return result;
  }

  private async syncPhoto(payload: unknown) {
    // Implement photo sync logic
    console.log('Syncing photo:', payload);
  }

  private async syncChecklist(payload: unknown) {
    // Implement checklist sync logic
    console.log('Syncing checklist:', payload);
  }

  startPeriodicSync(intervalMinutes: number = 5) {
    if (typeof window === 'undefined') return;

    this.periodicSyncConsumers += 1;

    if (this.syncInterval) {
      return;
    }
    
    this.syncInterval = window.setInterval(() => {
      if (navigator.onLine) {
        void this.triggerSync();
      }
    }, intervalMinutes * 60 * 1000);
  }

  stopPeriodicSync() {
    this.periodicSyncConsumers = Math.max(0, this.periodicSyncConsumers - 1);

    if (this.periodicSyncConsumers > 0) {
      return;
    }

    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }
}

export const backgroundSync = new BackgroundSyncManager();
